'use client';

/**
 * Service Worker Registration Component
 *
 * Handles PWA service worker registration and lifecycle management
 * Security-first approach with user consent for updates
 */

import { useEffect, useCallback, useState } from 'react';
import { logger, toSerializable } from '@/lib/monitoring/logger';
import { triggerManualSync } from '@/lib/pwa/background-sync';

const OFFLINE_ENABLED = process.env.NEXT_PUBLIC_ENABLE_OFFLINE_WORKSPACE === 'true';

const pwaLog = (message: string, data?: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    logger.info(`[PWA] ${message}`, data !== undefined ? toSerializable(data) : undefined);
  }
};

interface ServiceWorkerRegistrationProps {
  scope?: string;
  updateViaCache?: 'all' | 'imports' | 'none';
  onRegistrationSuccess?: (registration: ServiceWorkerRegistration) => void;
  onRegistrationError?: (error: Error) => void;
  onUpdateAvailable?: (registration: ServiceWorkerRegistration) => void;
}

export function ServiceWorkerRegistration({
  scope = '/protected/',
  updateViaCache = 'none',
  onRegistrationSuccess,
  onRegistrationError,
  onUpdateAvailable
}: ServiceWorkerRegistrationProps) {
  if (!OFFLINE_ENABLED) {
    return null;
  }
  // Check service worker support
  const checkSupport = useCallback(() => {
    return 'serviceWorker' in navigator;
  }, []);

  // Set up service worker update detection
  const setupUpdateDetection = useCallback((registration: ServiceWorkerRegistration) => {
    // Check for waiting service worker (update available)
    if (registration.waiting) {
      pwaLog('Service worker update available');
      onUpdateAvailable?.(registration);
    }

    // Listen for new service worker installing
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      pwaLog('New service worker installing');

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New version available
            pwaLog('New service worker installed, update available');
            onUpdateAvailable?.(registration);
          } else {
            // First install
            pwaLog('Service worker installed for the first time');
          }
        }
      });
    });

    // Listen for controller change (after update applied)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      pwaLog('Service worker controller changed, reloading');
      window.location.reload();
    });

  }, [onUpdateAvailable]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!checkSupport()) {
      const error = new Error('Service Worker not supported');
      onRegistrationError?.(error);
      return;
    }

    try {
      pwaLog('Registering service worker');

      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope,
        updateViaCache
      });

      pwaLog('Service worker registered', { scope: registration.scope });

      onRegistrationSuccess?.(registration);

      // Set up update detection
      setupUpdateDetection(registration);

    } catch (error) {
      logger.error('Service worker registration failed', error);
      onRegistrationError?.(error as Error);
    }
  }, [checkSupport, setupUpdateDetection, scope, updateViaCache, onRegistrationSuccess, onRegistrationError]);

  // Register service worker on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      registerServiceWorker();
    }
  }, [registerServiceWorker]);

  // Development-only: Handle hot reloads
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Allow service worker in development for PWA testing
      // Just skip caching to avoid hot reload issues
      pwaLog('Allowing service worker registration in development for PWA testing');
    }
  }, []);

  // Listen for SW messages (e.g., SYNC_MUTATIONS)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.action === 'SYNC_MUTATIONS') {
        void triggerManualSync();
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Don't render anything - this is a utility component
  return null;
}

// Hook for accessing service worker state
export function useServiceWorker() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(setRegistration);
    }
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (registration) {
      await registration.update();
    }
  }, [registration]);

  const applyUpdate = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ action: 'SKIP_WAITING' });
    }
  }, [registration]);

  return {
    registration,
    isUpdateAvailable: Boolean(registration?.waiting),
    checkForUpdate,
    applyUpdate,
    isSupported: 'serviceWorker' in navigator
  };
}
